// Playbooks — re-org to a two-tab pack→story flow.
//
// Why the rewrite:
//   * Stories are the unit a seller actually narrates. There is one
//     playbook per (pack, story) — not a curated handful per pack.
//   * The original list mixed hand-authored playbooks with AI-generated
//     ones; from the user's POV that's confusing. Now we always pivot
//     on the selected pack and surface every story we know how to land
//     for that pack.
//   * URL shape is `/playbooks/{packId}__{storyId}` so deep links
//     keep working and the Coach can hand off into a specific story
//     playbook.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { api } from "@/lib/api";
import { useUiStore } from "@/lib/store/uiStore";
import { PACKS, PLAYBOOK_CONTENT, STORIES } from "@/lib/data";
import type { Profile, ScriptItem, ScriptType, Story } from "@/lib/types";
import { PackLogo } from "@/components/common/PackLogo";

type Tab = "pack" | "stories";

// ── Real backend playbook shape ──────────────────────────────────────
// Mirrors what `/api/packs/{id}/playbook` returns. We keep this loose
// because hand-rolled file packs and AI-generated packs share the
// general shape but vary in optional keys.
interface RawScriptStep {
  step?: number;
  action?: string;
  dialogue?: string;
}
interface RawDataBadge {
  label?: string;
  value?: string;
}
interface RawDataShape {
  description?: string;
  badges?: RawDataBadge[];
}
interface RawStory {
  id: string;
  configKey?: string;
  storyNumber?: number;
  title?: string;
  category?: string;
  persona?: string;
  narrative?: string;
  closingLine?: string;
  duration?: string;
  demoScript?: RawScriptStep[];
  dataShape?: RawDataShape;
}
interface RawPackPlaybook {
  salesCloud?: { stories?: RawStory[] };
  serviceCloud?: { stories?: RawStory[] };
  commands?: {
    sales?: { label?: string; cmd?: string }[];
    service?: { label?: string; cmd?: string }[];
  };
  combos?: { name?: string; useCase?: string; stories?: string[] }[];
}

function findRealStoryById(p: RawPackPlaybook, id: string): RawStory | null {
  const all = [
    ...(p.salesCloud?.stories ?? []),
    ...(p.serviceCloud?.stories ?? []),
  ];
  return (
    all.find((s) => s.id === id) ??
    all.find((s) => s.configKey === id) ??
    null
  );
}

// Adapt the real backend story shape into the local rendering shape so
// the existing UI components (script block, callouts, closing card)
// keep working without rewrites. Returns null-safe defaults.
function adaptRealStory(raw: RawStory): {
  id: string;
  title: string;
  duration: string;
  narrative: string;
  audience?: Record<string, string>;
  script: ScriptItem[];
  closingLine: string;
  objections: { q: string; a: string }[];
} {
  const script: ScriptItem[] = (raw.demoScript ?? []).flatMap((step) => {
    const items: ScriptItem[] = [];
    if (step.action) items.push({ type: "click", text: step.action });
    if (step.dialogue) items.push({ type: "say", text: step.dialogue });
    return items;
  });
  return {
    id: raw.id,
    title: raw.title ?? raw.id,
    duration: raw.duration ?? "10 min",
    narrative: raw.narrative ?? "",
    script,
    closingLine: raw.closingLine ?? "",
    // Real backend has a single persona per story, not three audience
    // cuts. Leave audience undefined so the UI hides the toggle and
    // shows persona as a chip instead.
    audience: undefined,
    // Backend doesn't ship objections per story today; leave empty
    // and let the UI hide the section. (Adding objection mining is a
    // separate workitem.)
    objections: [],
  };
}

const PERSISTED_PACK_KEY = "qb-v2-playbooks-pack";

export function Playbooks(): JSX.Element {
  const navigate = useNavigate();
  const openPlaybookGen = useUiStore((s) => s.setPlaybookGenOpen);
  const [tab, setTab] = useState<Tab>("pack");

  const { data: packs = [] } = useQuery({
    queryKey: ["profiles"],
    queryFn: api.listProfiles,
  });

  const [packId, setPackId] = useState<string>(() => {
    const stored = sessionStorage.getItem(PERSISTED_PACK_KEY);
    return stored ?? "";
  });

  // Default to the first pack once loaded.
  useEffect(() => {
    if (!packId && packs.length > 0) {
      const first = packs.find((p) => p.id === "cirrus_renewable_energy") ?? packs[0];
      setPackId(first.id);
    }
  }, [packs, packId]);

  useEffect(() => {
    if (packId) sessionStorage.setItem(PERSISTED_PACK_KEY, packId);
  }, [packId]);

  const pack = packs.find((p) => p.id === packId) ?? null;
  const stories = STORIES; // story library is global; pack data customizes content/playbook copy.

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="Playbooks"
        title="Demo playbooks"
        astro={{ pose: "welcome", message: "Got a meeting coming?" }}
        subtitle={`One playbook per story · pick a pack, then drill into the story you'll narrate.`}
        iconGlyph="▤"
        rightSlot={
          <button
            className="btn primary lg"
            style={{
              background: "linear-gradient(135deg, #fff 0%, #F6FAFF 100%)",
              color: "var(--eb-15)",
              border: "none",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              boxShadow:
                "0 2px 10px rgba(29,112,201,.25), inset 0 0 0 1px rgba(29,112,201,.15)",
            }}
            onClick={() => openPlaybookGen(true)}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background:
                  "linear-gradient(135deg, var(--eb-50), var(--violet))",
                color: "white",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              ✦
            </span>
            AI-generated playbook
          </button>
        }
      />

      {/* Tab strip */}
      <div
        style={{
          display: "flex",
          marginTop: 20,
          background: "white",
          border: "1px solid var(--bd)",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {(
          [
            { id: "pack", label: "1 · Pack", subLabel: pack ? pack.name : "Pick one" },
            {
              id: "stories",
              label: "2 · Stories to narrate",
              subLabel: pack ? `${stories.length} stories` : "Pick a pack first",
              disabled: !pack,
            },
          ] as { id: Tab; label: string; subLabel: string; disabled?: boolean }[]
        ).map((t, i) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              style={{
                flex: 1,
                padding: "14px 18px",
                border: 0,
                borderRight: i === 0 ? "1px solid var(--bd-2)" : 0,
                background: active ? "var(--cb-98)" : "white",
                cursor: t.disabled ? "not-allowed" : "pointer",
                opacity: t.disabled ? 0.6 : 1,
                textAlign: "left",
                position: "relative",
              }}
            >
              {active && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: "var(--eb-50)",
                  }}
                />
              )}
              <div className="label">{t.label}</div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? "var(--eb-15)" : "var(--ink)",
                  marginTop: 2,
                }}
              >
                {t.subLabel}
              </div>
            </button>
          );
        })}
      </div>

      {tab === "pack" && (
        <PackPicker
          packs={packs}
          selected={packId}
          onPick={(id) => {
            setPackId(id);
            setTab("stories");
          }}
        />
      )}
      {tab === "stories" && pack && (
        <StoryGrid
          pack={pack}
          stories={stories}
          onPick={(storyId) => navigate(`/playbooks/${pack.id}__${storyId}`)}
        />
      )}
    </div>
  );
}

function PackPicker({
  packs,
  selected,
  onPick,
}: {
  packs: Profile[];
  selected: string;
  onPick: (id: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 12,
      }}
    >
      {packs.map((p) => {
        const sel = p.id === selected;
        return (
          <button
            key={p.id}
            onClick={() => onPick(p.id)}
            className="card"
            style={{
              padding: 18,
              textAlign: "left",
              cursor: "pointer",
              border: sel ? "1px solid var(--eb-50)" : undefined,
              boxShadow: sel ? "0 4px 14px -4px rgba(1,118,211,.3)" : undefined,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <PackLogo profile={p} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {p.industry}
                </div>
              </div>
              {sel && (
                <span className="chip on" style={{ fontSize: 10 }}>✓</span>
              )}
            </div>
            <p
              style={{
                fontSize: 12,
                color: "var(--ink-2)",
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              {p.description ?? "—"}
            </p>
          </button>
        );
      })}
    </div>
  );
}

function StoryGrid({
  pack,
  stories,
  onPick,
}: {
  pack: Profile;
  stories: Story[];
  onPick: (storyId: string) => void;
}): JSX.Element {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          padding: "10px 14px",
          background: "var(--cb-98)",
          borderRadius: 8,
          marginBottom: 14,
          fontSize: 12,
          color: "var(--ink-2)",
        }}
      >
        Showing playbooks for <strong>{pack.name}</strong>. Each story below
        opens a step-by-step demo script tailored to the pack.
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {stories.map((s) => (
          <button
            key={s.id}
            onClick={() => onPick(s.id)}
            className="card"
            style={{
              textAlign: "left",
              padding: 16,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                className="chip"
                style={{
                  fontSize: 9,
                  background:
                    s.cloud === "sales" ? "#eef6fc" : "#f6eefd",
                  color:
                    s.cloud === "sales" ? "var(--eb-50)" : "var(--violet)",
                  borderColor:
                    s.cloud === "sales" ? "#cce3f6" : "#d2bcf3",
                }}
              >
                {s.cloud}
              </span>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ink)",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {s.title}
              </div>
            </div>
            <p
              style={{
                fontSize: 12,
                color: "var(--ink-2)",
                margin: 0,
                lineHeight: 1.5,
                flex: 1,
              }}
            >
              {s.desc}
            </p>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {s.tags.map((t) => (
                <span key={t} className="chip ghost" style={{ fontSize: 10 }}>
                  {t}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Detail ───────────────────────────────────────────────────────
export function PlaybookDetail(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  // New URL shape: `{packId}__{storyId}`. Old URL shape: a free-form
  // playbook id from the legacy PLAYBOOKS list. Support both so old
  // bookmarks don't 404.
  const [packId, storyId] = id.includes("__") ? id.split("__", 2) : ["", ""];

  const { data: packs = [] } = useQuery({
    queryKey: ["profiles"],
    queryFn: api.listProfiles,
  });
  const pack = packs.find((p) => p.id === packId) ?? PACKS[0];
  const story = STORIES.find((s) => s.id === storyId) ?? STORIES[0];

  // Try to load the AI-generated playbook for the pack, if one exists.
  // The hand-authored PLAYBOOK_CONTENT is the fallback used for packs
  // that haven't had a playbook generated yet.
  const { data: aiPlaybook } = useQuery<RawPackPlaybook | null>({
    queryKey: ["pack-playbook", packId],
    queryFn: async () => {
      const r = await fetch(`/api/packs/${packId}/playbook`, { credentials: "include" });
      if (!r.ok) return null;
      return (await r.json()) as RawPackPlaybook;
    },
    enabled: !!packId,
    retry: false,
  });

  // Build the story payload to render. Prefer the real per-pack
  // playbook (with story-specific narrative, demoScript, dataShape,
  // closingLine, persona) and only fall back to the hand-authored
  // PLAYBOOK_CONTENT for the three legacy stories that ship that data.
  // Track whether what we're rendering actually corresponds to the
  // requested storyId so we can surface a banner instead of silently
  // showing a different story (audit §5.4).
  const { matched, isStoryMatch } = useMemo(() => {
    const fromBackend = aiPlaybook
      ? findRealStoryById(aiPlaybook, storyId)
      : null;
    if (fromBackend) {
      return { matched: adaptRealStory(fromBackend), isStoryMatch: true };
    }
    const legacy = PLAYBOOK_CONTENT.stories.find((s) => s.id === storyId);
    return {
      matched: legacy ?? PLAYBOOK_CONTENT.stories[0],
      isStoryMatch: !!legacy,
    };
  }, [aiPlaybook, storyId]);

  const fromBackendStory = aiPlaybook ? findRealStoryById(aiPlaybook, storyId) : null;
  const dataBadges = fromBackendStory?.dataShape?.badges ?? [];

  // Audience tabs only make sense when the legacy hand-authored content
  // is in play; the real backend payload has a single `persona` per
  // story. Show the tabs only for stories that ship audience copy, hide
  // them otherwise so we never render a misleading "for CEO" frame
  // that's actually identical for all roles.
  const audiences = useMemo(
    () => Object.keys(matched.audience ?? {}),
    [matched.audience],
  );
  const [activeAudience, setActiveAudience] = useState<string>(audiences[0] ?? "");
  useEffect(() => {
    if (audiences.length > 0 && !audiences.includes(activeAudience)) {
      setActiveAudience(audiences[0]);
    }
  }, [audiences, activeAudience]);

  if (!pack || !story) {
    return (
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "60px 24px", textAlign: "center", color: "var(--ink-3)" }}>
        Loading playbook…
      </div>
    );
  }

  const audienceCopy = matched.audience
    ? matched.audience[activeAudience] ?? Object.values(matched.audience)[0]
    : null;
  const persona = fromBackendStory?.persona ?? null;

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb={
          <button
            onClick={() => navigate("/playbooks")}
            style={{
              background: "transparent",
              border: 0,
              color: "#AACBE2",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              padding: 0,
              cursor: "pointer",
            }}
          >
            ← Playbooks
          </button>
        }
        iconGlyph="▤"
        title={story.title}
        subtitle={`${pack.name} · ${pack.industry} · ${matched.duration} demo`}
        chips={[
          { label: story.cloud === "sales" ? "Sales Cloud" : "Service Cloud", bg: "rgba(255,255,255,.14)" },
          ...story.tags.map((t) => ({ label: t, bg: "rgba(255,255,255,.14)" })),
        ]}
        rightSlot={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            <button
              onClick={() => navigate("/coach")}
              className="btn ghost"
              style={{
                background: "linear-gradient(135deg, rgba(107,71,211,.35), rgba(29,112,201,.35))",
                color: "white",
                border: "1px solid rgba(255,255,255,.3)",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 13 }}>✦</span> AI Demo Coach
            </button>
            <a
              className="btn primary lg"
              href={`/api/packs/${encodeURIComponent(packId)}/playbook/html`}
              target="_blank"
              rel="noreferrer"
              style={{
                background: "#fff",
                color: "var(--eb-15)",
                border: "none",
                whiteSpace: "nowrap",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              ↗ Export HTML
            </a>
          </div>
        }
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(255,255,255,.08)",
            padding: 10,
            borderRadius: 10,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "#AACBE2",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontWeight: 700,
            }}
          >
            Audience
          </div>
          {audiences.length > 0 ? (
            <div style={{ display: "flex", gap: 4 }}>
              {audiences.map((a) => {
                const on = activeAudience === a;
                return (
                  <button
                    key={a}
                    onClick={() => setActiveAudience(a)}
                    style={{
                      padding: "5px 12px",
                      fontSize: 12,
                      background: on ? "white" : "transparent",
                      color: on ? "var(--eb-15)" : "white",
                      border: on ? 0 : "1px solid rgba(255,255,255,.25)",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          ) : persona ? (
            <span
              style={{
                padding: "5px 12px",
                fontSize: 12,
                background: "rgba(255,255,255,.15)",
                color: "white",
                borderRadius: 999,
                fontWeight: 600,
              }}
            >
              For {persona}
            </span>
          ) : null}
          {aiPlaybook && (
            <div
              style={{
                marginLeft: "auto",
                fontSize: 11,
                color: "#AACBE2",
              }}
            >
              ✦ AI-tailored playbook for {pack.name}
            </div>
          )}
        </div>
      </MissionControlHero>

      {!isStoryMatch && (
        <div
          role="status"
          style={{
            marginTop: 16,
            padding: "10px 14px",
            background: "var(--chip-yellow-bg)",
            color: "var(--chip-yellow-fg)",
            border: "1px solid #f6d491",
            borderRadius: 10,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span aria-hidden="true">⚠︎</span>
          <span>
            No tailored playbook for <b>{story.title}</b> yet — showing the
            closest available script for this pack so you can still preview
            the flow. Generate a playbook for this pack to see story-specific
            content.
          </span>
        </div>
      )}

      <div
        style={{
          marginTop: 20,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 20,
        }}
      >
        {/* Story narrative */}
        {matched.narrative && (
          <div
            className="card"
            style={{
              padding: 16,
              background: "linear-gradient(135deg, #fff8e0, white)",
              border: "1px solid #f5e3a8",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#8a5200",
                textTransform: "uppercase",
                letterSpacing: ".08em",
                marginBottom: 6,
              }}
            >
              The story arc
            </div>
            <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.6, fontStyle: "italic" }}>
              {matched.narrative}
            </div>
          </div>
        )}

        {/* Audience copy (only when hand-authored audience cuts exist) */}
        {audienceCopy && (
          <div
            className="card"
            style={{
              padding: 14,
              background: "#f0f7fc",
              border: "1px solid #cce3f6",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "var(--eb-50)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              For {activeAudience}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.5 }}>
              {audienceCopy}
            </div>
          </div>
        )}

        {/* Data shape badges (from real backend playbook only) */}
        {dataBadges.length > 0 && (
          <div
            className="card"
            style={{
              padding: 14,
              background: "white",
              border: "1px solid var(--bd-2)",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              What the data shows
            </div>
            {fromBackendStory?.dataShape?.description && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-2)",
                  lineHeight: 1.5,
                  marginBottom: 10,
                }}
              >
                {fromBackendStory.dataShape.description}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {dataBadges.map((b, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11,
                    padding: "4px 10px",
                    background: "var(--cb-95)",
                    border: "1px solid var(--cb-90)",
                    borderRadius: 999,
                    color: "var(--eb-15)",
                  }}
                >
                  <strong>{b.label}</strong>
                  {b.value ? `: ${b.value}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Demo script */}
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom: 10,
            }}
          >
            Full demo script
          </div>
          <ScriptBlock items={matched.script} />
        </div>

        {/* Closing */}
        {matched.closingLine && (
          <div
            style={{
              padding: "16px 18px",
              background: "linear-gradient(135deg, var(--eb-15), var(--eb-50))",
              borderRadius: 12,
              color: "white",
              boxShadow: "0 4px 16px -6px rgba(1,118,211,.4)",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "rgba(255,255,255,.75)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
                marginBottom: 6,
              }}
            >
              Key closing line
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, fontStyle: "italic" }}>
              "{matched.closingLine}"
            </div>
          </div>
        )}

        {/* Objections (legacy hand-authored content only) */}
        {matched.objections.length > 0 && (
        <Callout title="Likely objections">
          <div style={{ display: "grid", gap: 10 }}>
            {matched.objections.map((o, i) => (
              <div key={i}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
                  "{o.q}"
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4, lineHeight: 1.5 }}>
                  → {o.a}
                </div>
              </div>
            ))}
          </div>
        </Callout>
        )}
      </div>
    </div>
  );
}

const SCRIPT_LABEL: Record<ScriptType, string> = {
  say: "SAY",
  listen: "LISTEN",
  click: "DO",
  ask: "ASK",
};
const SCRIPT_COLOR: Record<ScriptType, string> = {
  say: "var(--eb-50)",
  listen: "var(--teal)",
  click: "#a06410",
  ask: "var(--pink)",
};

function ScriptBlock({ items }: { items: ScriptItem[] }): JSX.Element {
  return (
    <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
      {items.map((it, i) => {
        const c = SCRIPT_COLOR[it.type];
        return (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 12,
              padding: 14,
              background: "white",
              border: "1px solid var(--bd-2)",
              borderRadius: 10,
              borderLeft: `3px solid ${c}`,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: c,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                width: 48,
                flexShrink: 0,
                paddingTop: 2,
              }}
            >
              {SCRIPT_LABEL[it.type]}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.55, flex: 1 }}>
              {it.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Callout({
  title,
  tone = "blue",
  children,
}: {
  title: string;
  tone?: "blue" | "teal";
  children: React.ReactNode;
}): JSX.Element {
  const TONES = {
    blue: { bg: "var(--cb-95)", bd: "var(--cb-90)", fg: "var(--eb-50)" },
    teal: { bg: "#ebf5f0", bd: "#c0e0d2", fg: "var(--teal)" },
  } as const;
  const t = TONES[tone];
  return (
    <div
      style={{
        padding: 16,
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: 10,
        marginBottom: 18,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: t.fg,
          textTransform: "uppercase",
          letterSpacing: ".08em",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}
